import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  DatePicker,
  Select,
  Statistic,
  Table,
  Radio,
  Button,
  Space,
  Empty,
  message,
} from "antd";
import moment, { Moment } from "moment";
import {
  fetchAdminDashboard,
  DashboardResponseData,
  DashboardSalesChannel,
  findEventsFromPage121,
  FindEventItem,
} from "@src/services/dashboard";
import {
  DollarCircleOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
  TeamOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";
import "moment/locale/vi";

const { Title, Text } = Typography;

const { RangePicker } = DatePicker;

const COLORS = [
  "#1890ff",
  "#52c41a",
  "#faad14",
  "#722ed1",
  "#13c2c2",
  "#fadb14",
];

const styles = {
  container: {
    padding: "24px",
    background: "#f0f2f5",
    minHeight: "100vh",
  },
  card: {
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "none",
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  statsCard: (gradient: string) => ({
    background: gradient,
    color: "#fff",
  }),
  cardHover: {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  },
  statisticIcon: (bg: string) => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(4px)",
  }),
  titleWhite: {
    color: "#fff",
    opacity: 0.85,
    fontSize: 13,
    display: "block",
    marginBottom: 4,
  },
  valueWhite: {
    color: "#fff",
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.2,
  },
};

const GRADIENTS = {
  blue: "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)",
  green: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
  orange: "linear-gradient(135deg, #faad14 0%, #d48806 100%)",
  purple: "linear-gradient(135deg, #722ed1 0%, #531dab 100%)",
};

const currencyFormatter = new Intl.NumberFormat("vi-VN", {
  style: "currency",
  currency: "VND",
  maximumFractionDigits: 0,
});

const Home: React.FC = () => {
  const [range, setRange] = useState<[Moment, Moment]>([
    moment().startOf("day").subtract(6, "days"),
    moment().endOf("day"),
  ]);
  const [quickRange, setQuickRange] = useState<"7D" | "30D" | "TODAY">("7D");
  const [eventId, setEventId] = useState<number | undefined>(undefined);
  const [salesChannel, setSalesChannel] = useState<
    DashboardSalesChannel | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<DashboardResponseData["data"] | undefined>(
    undefined
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const body = {
        from: range?.[0]?.valueOf(),
        to: range?.[1]?.valueOf(),
        eventId,
        salesChannel,
      };
      const res = await fetchAdminDashboard(body);
      if (res?.data?.code === 0) {
        setData(res.data.data);
      } else {
        message.error("Không thể tải dữ liệu dashboard");
      }
    } catch (e) {
      message.error("Có lỗi xảy ra khi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, [range, eventId, salesChannel]);

  useEffect(() => {
    // initial load
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [eventOptions, setEventOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [eventSkip, setEventSkip] = useState<number>(0);
  const [eventHasMore, setEventHasMore] = useState<boolean>(true);
  const [eventLoading, setEventLoading] = useState<boolean>(false);
  const [eventSearch, setEventSearch] = useState<string>("");
  const EVENT_LIMIT = 20;

  const fetchEventOptions = useCallback(
    async (q?: string, append: boolean = false) => {
      try {
        setEventLoading(true);
        const currentSkip = append ? eventSkip : 0;
        const { items, total } = await findEventsFromPage121({
          search: q,
          skip: currentSkip,
          limit: EVENT_LIMIT,
        });
        const mapped = (items as FindEventItem[]).map((e: FindEventItem) => ({
          label: e.nameEvent,
          value: e.id,
        }));
        if (append) {
          setEventOptions((prev) => {
            const merged = [...prev, ...mapped];
            const dedup = Array.from(
              new Map(merged.map((it) => [it.value, it])).values()
            );
            return dedup;
          });
        } else {
          setEventOptions(mapped);
        }
        const newSkip = currentSkip + items.length;
        setEventSkip(newSkip);
        setEventHasMore(newSkip < (total || 0));
      } catch (e) {
        // ignore
      } finally {
        setEventLoading(false);
      }
    },
    [eventSkip]
  );

  useEffect(() => {
    fetchEventOptions("", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const soldPieData = useMemo(() => {
    const online = data?.summary?.ticket?.sold?.online || 0;
    const invitation = data?.summary?.ticket?.sold?.invitation || 0;
    return [
      { name: "ONLINE", value: online },
      { name: "INVITATION", value: invitation },
    ];
  }, [data]);

  const onApplyQuickRange = useCallback((val: "7D" | "30D" | "TODAY") => {
    setQuickRange(val);
    if (val === "TODAY") {
      setRange([moment().startOf("day"), moment().endOf("day")]);
    } else if (val === "30D") {
      setRange([
        moment().startOf("day").subtract(29, "days"),
        moment().endOf("day"),
      ]);
    } else {
      setRange([
        moment().startOf("day").subtract(6, "days"),
        moment().endOf("day"),
      ]);
    }
  }, []);

  const hasSoldData =
    (soldPieData?.[0]?.value || 0) + (soldPieData?.[1]?.value || 0) > 0;

  return (
    <div className="dashboard-container" style={styles.container}>
      <Row gutter={[16, 8]}>
        <Col span={24}>
          <Title level={2}>Trang chủ</Title>
          <Text type="secondary">
            {moment().locale("vi").format("dddd, DD/MM/YYYY")}
          </Text>
        </Col>
      </Row>

      <Card
        style={{ ...styles.card, marginTop: 8, height: "auto" }}
        bordered={false}
      >
        <Row gutter={[12, 12]} align="middle" justify="space-between">
          <Col>
            <Space size={12} wrap>
              <Radio.Group
                value={quickRange}
                onChange={(e) =>
                  onApplyQuickRange(e.target.value as "7D" | "30D" | "TODAY")
                }
                buttonStyle="solid"
              >
                <Radio.Button value="TODAY">Hôm nay</Radio.Button>
                <Radio.Button value="7D">7 ngày</Radio.Button>
                <Radio.Button value="30D">30 ngày</Radio.Button>
              </Radio.Group>
              <RangePicker
                value={range}
                onChange={(vals) => {
                  if (vals && vals[0] && vals[1]) {
                    setRange([vals[0] as Moment, vals[1] as Moment]);
                  }
                }}
                allowClear={false}
                format="DD/MM/YYYY"
              />
              <Select
                allowClear
                placeholder="Chọn sự kiện"
                style={{ minWidth: 220 }}
                value={eventId}
                showSearch
                filterOption={false}
                onSearch={(val) => {
                  setEventSearch(val);
                  setEventSkip(0);
                  setEventHasMore(true);
                  fetchEventOptions(val, false);
                }}
                onPopupScroll={(e) => {
                  const target = e.target as HTMLDivElement;
                  if (
                    !eventLoading &&
                    eventHasMore &&
                    target.scrollTop + target.clientHeight >=
                      target.scrollHeight - 8
                  ) {
                    fetchEventOptions(eventSearch, true);
                  }
                }}
                onChange={(v?: number) => setEventId(v)}
                options={eventOptions}
              />
              <Select
                allowClear
                placeholder="Kênh bán"
                style={{ minWidth: 180 }}
                value={salesChannel}
                onChange={(v?: DashboardSalesChannel) => setSalesChannel(v)}
                options={[
                  { label: "ONLINE", value: "ONLINE" },
                  { label: "INVITATION", value: "INVITATION" },
                ]}
              />
              <Button type="primary" onClick={loadData} loading={loading}>
                Áp dụng
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12} lg={6}>
          <Card
            bordered={false}
            loading={loading}
            style={{ ...styles.card, ...styles.statsCard(GRADIENTS.blue) }}
            bodyStyle={{ padding: "12px 16px" }}
            hoverable
          >
            <Row align="middle" gutter={12}>
              <Col>
                <div style={styles.statisticIcon("")}>
                  <DollarCircleOutlined />
                </div>
              </Col>
              <Col flex="auto">
                <Statistic
                  title={<span style={styles.titleWhite}>Doanh thu vé</span>}
                  value={data?.summary?.ticket?.revenue || 0}
                  formatter={(val) => currencyFormatter.format(Number(val))}
                  valueStyle={styles.valueWhite}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card
            bordered={false}
            loading={loading}
            style={{ ...styles.card, ...styles.statsCard(GRADIENTS.green) }}
            bodyStyle={{ padding: "12px 16px" }}
            hoverable
          >
            <Row align="middle" gutter={12}>
              <Col>
                <div style={styles.statisticIcon("")}>
                  <ShoppingCartOutlined />
                </div>
              </Col>
              <Col flex="auto">
                <Statistic
                  title={
                    <span style={styles.titleWhite}>Đơn vé thành công</span>
                  }
                  value={data?.summary?.ticket?.successOrders || 0}
                  valueStyle={styles.valueWhite}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card
            bordered={false}
            loading={loading}
            style={{ ...styles.card, ...styles.statsCard(GRADIENTS.orange) }}
            bodyStyle={{ padding: "12px 16px" }}
            hoverable
          >
            <Row align="middle" gutter={12}>
              <Col>
                <div style={styles.statisticIcon("")}>
                  <CheckCircleOutlined />
                </div>
              </Col>
              <Col flex="auto">
                <Statistic
                  title={<span style={styles.titleWhite}>Lượt check-in</span>}
                  value={data?.summary?.ticket?.checkins || 0}
                  valueStyle={styles.valueWhite}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card
            bordered={false}
            loading={loading}
            style={{ ...styles.card, ...styles.statsCard(GRADIENTS.purple) }}
            bodyStyle={{ padding: "12px 16px" }}
            hoverable
          >
            <Row align="middle" gutter={12}>
              <Col>
                <div style={styles.statisticIcon("")}>
                  <AppstoreOutlined />
                </div>
              </Col>
              <Col flex="auto">
                <Statistic
                  title={
                    <span style={styles.titleWhite}>Danh thu sản phẩm</span>
                  }
                  value={data?.summary?.product?.revenue || 0}
                  formatter={(val) => currencyFormatter.format(Number(val))}
                  valueStyle={styles.valueWhite}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 4, opacity: 0.85 }}>
              <Text style={{ color: "#fff", fontSize: 12 }}>
                <ShoppingCartOutlined />{" "}
                {data?.summary?.product?.successOrders || 0} đơn
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
        <Col xs={24} lg={12}>
          <Card
            title="Doanh thu theo thời gian"
            bordered={false}
            loading={loading}
            style={styles.card}
          >
            <div
              style={{
                width: "100%",
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {(data?.charts?.revenueOverTime?.length || 0) > 0 ? (
                <ResponsiveContainer>
                  <AreaChart data={data?.charts?.revenueOverTime}>
                    <defs>
                      <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="5%"
                          stopColor="#1890ff"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#1890ff"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e8e8e8"
                    />
                    <XAxis
                      dataKey="period"
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tickFormatter={(val) => `${val / 1000}k`}
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                      formatter={(val: number) => currencyFormatter.format(val)}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#1890ff"
                      fillOpacity={1}
                      fill="url(#colorRev)"
                      name="Doanh thu"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Không có dữ liệu"
                />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title="Đơn hàng theo thời gian"
            bordered={false}
            loading={loading}
            style={styles.card}
          >
            <div
              style={{
                width: "100%",
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {(data?.charts?.ordersOverTime?.length || 0) > 0 ? (
                <ResponsiveContainer>
                  <BarChart data={data?.charts?.ordersOverTime}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e8e8e8"
                    />
                    <XAxis
                      dataKey="period"
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="online"
                      stackId="a"
                      fill="#1890ff"
                      name="Online"
                    />
                    <Bar
                      dataKey="invitation"
                      stackId="a"
                      fill="#faad14"
                      name="Mời"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Không có dữ liệu"
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={10}>
          <Card
            title={
              <Space>
                <TeamOutlined /> Phân bổ kênh bán vé
              </Space>
            }
            bordered={false}
            loading={loading}
            style={styles.card}
          >
            {hasSoldData ? (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      dataKey="value"
                      data={soldPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      label
                    >
                      {soldPieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend verticalAlign="bottom" iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Không có dữ liệu"
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card
            title="Sự kiện tiêu biểu"
            bordered={false}
            loading={loading}
            style={styles.card}
          >
            <Table
              size="small"
              rowKey={(r) => String(r.eventId)}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Không có dữ liệu"
                  />
                ),
              }}
              columns={[
                { title: "Sự kiện", dataIndex: "name", key: "name" },
                {
                  title: "Vé bán",
                  dataIndex: "sold",
                  key: "sold",
                  align: "right",
                  render: (v) => v?.toLocaleString(),
                },
                {
                  title: "Doanh thu",
                  dataIndex: "revenue",
                  key: "revenue",
                  align: "right",
                  render: (v) => currencyFormatter.format(v || 0),
                },
              ]}
              dataSource={[...(data?.charts?.topEvents || [])]}
              pagination={{ pageSize: 5, showSizeChanger: false }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <CheckCircleOutlined /> Check-in theo thời gian
              </Space>
            }
            bordered={false}
            loading={loading}
            style={styles.card}
          >
            <div
              style={{
                width: "100%",
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {(data?.charts?.checkinOverTime?.length || 0) > 0 ? (
                <ResponsiveContainer>
                  <LineChart data={data?.charts?.checkinOverTime}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e8e8e8"
                    />
                    <XAxis
                      dataKey="period"
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="checkins"
                      stroke="#52c41a"
                      strokeWidth={3}
                      dot={{ r: 4, strokeWidth: 2, fill: "#fff" }}
                      activeDot={{ r: 6 }}
                      name="Lượt check-in"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Không có dữ liệu"
                />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BarChartOutlined /> Doanh thu theo loại vé
              </Space>
            }
            bordered={false}
            loading={loading}
            style={styles.card}
          >
            <div
              style={{
                width: "100%",
                height: 300,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {(data?.charts?.ticketsByType?.length || 0) > 0 ? (
                <ResponsiveContainer>
                  <BarChart
                    data={data?.charts?.ticketsByType}
                    layout="vertical"
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      horizontal={false}
                      stroke="#e8e8e8"
                    />
                    <XAxis type="number" hide />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={100}
                      stroke="#8c8c8c"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                      formatter={(v: any) => currencyFormatter.format(v)}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="#722ed1"
                      radius={[0, 4, 4, 0]}
                      name="Doanh thu"
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Không có dữ liệu"
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Home;
